import React, { useState } from "react";
import { SquarePlus } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "./dialog";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { queryClient, trpc } from "@/utils/trpc";
import { useRouter } from "@tanstack/react-router";

interface CreateWorkspaceDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const CreateWorkspaceDialog: React.FC<CreateWorkspaceDialogProps> = ({
	open,
	onOpenChange,
}) => {
	const workspacesQueryKey = trpc.workspace.getUserWorkspaces.queryKey();

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [error, setError] = useState<string | null>(null);

	const createWorkspaceMutation = useMutation(
		trpc.workspace.create.mutationOptions({
			onSuccess: (data) => {
				queryClient.invalidateQueries({ queryKey: workspacesQueryKey });
				onOpenChange(false);
				setName("");
				setDescription("");
			},
			onError: (error) => {
				setError(error.message || "Failed to create workspace");
			},
		}),
	);

	const handleCreateWorkspace = async () => {
		if (name.length < 4) {
			setError("Workspace name must be more than 3 characters.");
			return;
		}

		if (description.length > 0) {
			createWorkspaceMutation.mutate({ name, description });
		} else {
			createWorkspaceMutation.mutate({ name });
		}
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			setName("");
			setDescription("");
			setError(null);
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[500px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<SquarePlus className="h-5 w-5" />
						Create a new workspace
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<label
							htmlFor="workspace-name"
							className="mb-1 block font-medium text-sm"
						>
							Name <span className="text-red-500">*</span>
						</label>
						<Input
							id="workspace-name"
							value={name}
							onChange={e => setName(e.target.value)}
							placeholder="Workspace name"
							disabled={createWorkspaceMutation.isPending}
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="workspace-description">Description</Label>
						<Input
							id="workspace-description"
							value={description}
							onChange={e => setDescription(e.target.value)}
							placeholder="(Optional)"
							disabled={createWorkspaceMutation.isPending}
						/>
					</div>
					{error && <div className="text-red-500 text-sm">{error}</div>}
					<Button
						onClick={handleCreateWorkspace}
						disabled={!name || createWorkspaceMutation.isPending}
						className="w-full cursor-pointer"
					>
						{createWorkspaceMutation.isPending ? "Creating..." : "Create Workspace"}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};
